import dateUtility from './date.utility.js'

function buildHash(filters = {}) {
  const keys = Object.keys(filters).sort()
  if (!keys.length) return ''
  return keys.map((key) => `${key}=${JSON.stringify(filters[key])}`).join('&')
}

function buildKey(scopeKey, domain, hash = '') {
  return `${scopeKey}::${domain}::${hash}`
}

function buildMeta({ scopeKey, cacheKey, ids, domain, generations, filters = {}, total = null, loaded = dateUtility.now() }) {
  return {
    cacheKey,
    scopeKey,
    ids: [...ids],
    domain,
    generations: ids.reduce((acc, id) => {
      const key = String(id)
      acc[key] = generations?.[key] ? { ...generations[key] } : {}
      return acc
    }, {}),
    filters: { ...filters },
    loaded,
    total,
  }
}

function isFresh(meta, { scopeKey, ids, domain, generations }) {
  if (!meta) return false
  if (meta.scopeKey !== scopeKey) return false
  if (meta.domain !== domain) return false

  const resolvedIds = ids?.length ? ids : meta.ids ?? []
  return resolvedIds.every((id) => {
    const key = String(id)
    return (generations?.[key]?.[domain] ?? 0) === (meta.generations?.[key]?.[domain] ?? 0)
  })
}

function mergeRowsById(current, next) {
  const nextById = new Map(next.map((row) => [String(row._id), row]))
  const merged = current.reduce((rows, row) => {
    const id = String(row._id)
    if (nextById.has(id)) rows.push(nextById.get(id))
    return rows
  }, [])
  const seen = new Set(merged.map((row) => String(row._id)))
  return [...merged, ...next.filter((row) => !seen.has(String(row._id)))]
}

function mergeGenerations(current = {}, next = {}) {
  return Object.entries(next).reduce(
    (acc, [id, domains]) => {
      acc[id] = { ...acc[id], ...domains }
      return acc
    },
    { ...current },
  )
}

function bumpGeneration(current = {}, accountId, domain) {
  if (accountId == null || accountId === '') return current
  const id = String(accountId)
  const count = (current[id]?.[domain] ?? 0) + 1
  return mergeGenerations(current, { [id]: { [domain]: count } })
}

class CacheUtility {
  /**
   * Stable hash for filter objects — sorted keys, JSON values.
   *
   * @param {object} [filters={}]
   * @returns {string}
   */
  hash(filters = {}) {
    return buildHash(filters)
  }

  /**
   * Build a cache bucket key — `scopeKey::domain::hash`.
   *
   * @param {string} scopeKey
   * @param {string} domain
   * @param {string} [hash='']
   * @returns {string}
   */
  key(scopeKey, domain, hash = '') {
    return buildKey(scopeKey, domain, hash)
  }

  /**
   * Build `_meta` for a hydrated cache slice — records scope, generation
   * snapshot, and the filters used for refetch.
   *
   * @param {object} options
   * @returns {object}
   */
  meta(options) {
    return buildMeta(options)
  }

  /**
   * Whether a cache bucket is still fresh — scope key match plus per-account
   * generation equality for the bucket domain.
   *
   * @param {object|null|undefined} meta
   * @param {{ scopeKey: string, ids: string[], domain: string, generations: object }} context
   * @returns {boolean}
   */
  fresh(meta, context) {
    return isFresh(meta, context)
  }

  merge = {
    /**
     * Merge a server list into the cached order — update rows in place, append
     * new ids, drop rows missing from the server response.
     *
     * @template {{ _id: string }} T
     * @param {T[]} current
     * @param {T[]} next
     * @returns {T[]}
     */
    id(current, next) {
      return mergeRowsById(current, next)
    },

    /**
     * Merge a `/sync/generations` patch into an existing generations map.
     *
     * @param {Record<string, Record<string, number>>} current
     * @param {Record<string, Record<string, number>>} next
     * @returns {Record<string, Record<string, number>>}
     */
    generations(current, next) {
      return mergeGenerations(current, next)
    },
  }

  generations = {
    /**
     * Increment one account's generation counter for a domain — used after a
     * successful local mutation so the session and slice snapshot stay fresh.
     *
     * @param {Record<string, Record<string, number>>} [current={}]
     * @param {string} accountId
     * @param {string} domain
     * @returns {Record<string, Record<string, number>>}
     */
    bump(current = {}, accountId, domain) {
      return bumpGeneration(current, accountId, domain)
    },
  }
}

export default new CacheUtility()
